import {
  ChangeEvent,
  FormEvent,
  FormEventHandler,
  MutableRefObject,
} from 'react';

import {
  themes,
  DeviceInfoSuccessResponse,
  FactsheetStoryblok,
  IpInfoSuccessResponse,
  Locales,
  MTGTimeslotItem,
  CommonMessages,
} from '@monoimin/sb-types';
import { SubscribePopupFormDict } from '@monoimin/intl';
import { FormButtonProps } from '@monoimin/atoms';

import formConfig from './mock-config.json';

export type FormConfig = typeof formConfig;

export type FormConfigEntry = {
  id: number;
  title: string;
  entries: number;
};

export type FieldConfig = {
  type: string;
  id: number;
  formId: number;
  label: string;
  adminLabel: string;
  isRequired: boolean;
  size?: string;
  errorMessage?: string;
  visibility?: string;
  inputs?: any[] | string | null;
  description?: string;
  allowsPrepopulate?: boolean;
  inputMask?: boolean;
  inputMaskValue?: string;
  inputMaskIsCustom?: boolean | string;
  maxLength?: string;
  inputType?: string;
  labelPlacement?: string;
  descriptionPlacement?: string;
  subLabelPlacement?: string;
  placeholder?: string;
  cssClass?: string;
  inputName?: string;
  noDuplicates?: boolean;
  defaultValue?: string;
  choices?: MultiSelectOptionItem[] | string;
  conditionalLogic?: InputConditionalLogic | string;
  productField?: string;
  multipleFiles?: boolean;
  maxFiles?: string;
  calculationFormula?: string;
  calculationRounding?: string;
  enableCalculation?: string;
  disableQuantity?: boolean;
  displayAllCategories?: boolean;
  useRichTextEditor?: boolean;
  pageNumber?: number;
  fields?: string;
  displayOnly?: boolean;
  layoutGroupId?: string;
  enableAutocomplete?: boolean;
  autocompleteAttribute?: string;
  layoutGridColumnSpan?: number;
  enableEnhancedUI?: number;
  errors?: any[];
  fieldID?: string;
  enableChoiceValue?: boolean;
  checkboxLabel?: string;
  emailConfirmEnabled?: string;
  phoneFormat?: string;
  form_id?: string;
  enableSelectAll?: string;
  enablePrice?: string;
  checked_indicator_url?: string;
  checked_indicator_markup?: string;
  descriptionPlaceholder?: string;
  validateState?: boolean;
  default_value?: string;
  content?: string;
  disableMargins?: string;
  enablePasswordInput?: string;
  numberFormat?: string;
  rangeMin?: string;
  rangeMax?: string;
  enableOtherChoice?: string;
  maxFileSize?: string;
  allowedExtensions?: string;
};

export type FormValuesById = Record<string, FormDataEntryValue>;

export interface InputProps extends FieldConfig {
  value?: FormDataEntryValue;
  formValues: FormValues;
  formValuesById: FormValuesById;
  locale: Locales;
  customId: string;
  withPlaceholder?: boolean;
  cssClass?: string;
  classNames?: {
    label?: string;
    inputWrapper?: string;
    input?: string;
    inputTag?: string;
    invalid?: string;
    inputContainer?: string;
    container?: string;
    icon?: string;
  };
  max?: string;
  min?: string;
  inputVariant?:
    | 'default'
    | 'dueDiligence'
    | 'scheduleMeeting'
    | 'meetingSurvey'
    | 'unsubscribe';
  fileUploads?: FileUploadsState;
  onChange?: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}

export type CustomOnSubmit = (
  e: FormEvent<HTMLFormElement>,
  isShowThankYou?: boolean
) => Promise<{ success: boolean; showThankYou?: () => void }>;

export interface CustomVariantProps
  extends Pick<InputProps, 'formValues' | 'formValuesById'> {
  formConfig: FormConfig;
  locale: Locales;
  onInput: FormEventHandler<HTMLFormElement>;
  onSubmit: CustomOnSubmit;
  formValues: FormValues;
  formRef: MutableRefObject<HTMLFormElement | null>;
  successMessage?: string | null;
  buttonProps?: FormButtonProps;
  hideForm?: boolean;
  subscribePopupFormDict?: SubscribePopupFormDict;
  theme?: themes;
  scheduleMeeting?: ScheduleMeetingFormProps;
  isLoading?: boolean;
  messages?: CommonMessages;
  variant?: string | undefined;
}

export interface ScheduleMeetingFormProps {
  managerName?: string;
  managerSlug?: string;
  userTimezone?: string;
  isOffline?: boolean;
  date?: string;
  time?: MTGTimeslotItem;
}

export interface MultiSelectOptionItem {
  text: string;
  value: string;
  isSelected: boolean;
  price: string;
}

export type FormValues = {
  [k: string]: FormDataEntryValue;
};

export type InputConditionalLogic = {
  enabled: boolean;
  actionType: string;
  logicType: string;
  rules: { fieldId: string; operator: string; value: string }[];
};

export type ValidateEmailSuccessResponse = {
  address: string;
  status: 'valid' | 'invalid';
  sub_status: string;
  free_email: boolean;
  did_you_mean: string | null;
  account: string | null;
  domain: string | null;
  domain_age_days: string | null;
  smtp_provider: string | null;
  mx_found: string;
  mx_record: string;
  firstname: string | null;
  lastname: string | null;
  gender: string | null;
  country: string | null;
  region: string | null;
  city: string | null;
  zipcode: string | null;
  processed_at: string;
};

export type IpFields = {
  ipcity: IpInfoSuccessResponse['ipCity'];
  ipcountry: IpInfoSuccessResponse['ipCountry'];
  fr_ip: IpInfoSuccessResponse['realIp'];
};

export type DataForCollection = {
  timestamp: string;
  context: {
    ads: Record<string, string | null>;
    page: {
      path: string;
      referrer: string;
      title: string;
      url: string;
      location: string;
      hostname: string;
    };
    device: {
      deviceDocEncoding: string;
      userAgent: string;
      deviceScreenWidth: number;
      deviceScreenHeight: number;
      deviceViewPortWidth: number;
      deviceViewPortHeight: number;
    };
    cookies: {
      ga: string | null;
      ym: string | null;
      fbp: string | null;
      fbc: string | null;
      localStorageAccessible: boolean;
      cookiesAccessible: boolean;
    };
    library: {
      name: string;
      version: string;
    };
    protocols: {
      environment: string;
      eventVersion: number;
    };
  };
  properties: FormRequestData | LogDataPayload;
  name: string;
  messageId: string;
  anonymousId: string;
  type: string;
  writeKey: string;
  userId: string | null;
  sentAt: string;
};

export type AnalyticsProps = {
  name: string;
  formType: string;
  leadType: string;
  programName?: string;
};

export type SubscribeFormProps = {
  className?: string;
  buttonProps?: Pick<
    FormButtonProps,
    'children' | 'variant' | 'className' | 'type' | 'theme'
  >;
  classNames?: {
    successMessageWrapper?: string;
    successMessageMessage?: string;
    successMessageBottomText?: string;
  };
  fieldSize?: 'small' | 'normal';
  isPopup?: boolean;
  onSubmitCb?: () => void;
  id?: string;
  show?: boolean;
  analytics: AnalyticsProps;
};

export type RequiredDataForLogRequest = {
  id?: SubscribeFormProps['id'];
  name?: string;
  locale: Locales;
  validateEmailResult: ValidateEmailSuccessResponse | null;
  deviceResult?: DeviceInfoSuccessResponse;
  ipResult: IpInfoSuccessResponse | null;
  formType?: string;
  programName?: string;
  leadType?: string;
};

export type FormRequestFrFields = {
  fr_program_name?: string;
  fr_date_created?: string;
  fr_form_id?: string;
  fr_source_url?: string;
  fr_ip?: string;
  fr_form_title?: string;
  fr_program_country?: string;
};

export type FormSfFields = {
  sf_status: string;
  sf_currency_iso_code: string;
};

/**
 * Поля из Zero Bounce
 */
export type FormZbFields = {
  zb_domain?: ValidateEmailSuccessResponse['domain'];
  zb_free_email?: ValidateEmailSuccessResponse['free_email'];
  zb_smtp_provider?: ValidateEmailSuccessResponse['smtp_provider'];
  zb_status?: ValidateEmailSuccessResponse['status'];
  zb_sub_status?: ValidateEmailSuccessResponse['sub_status'];
  zb_account?: ValidateEmailSuccessResponse['account'];
};

export type FormRequestData =
  | FormRequestFrFields &
      FormSfFields &
      FormZbFields & {
        user_email?: string;
        language_form: string;
        ga_cookie_id: string | null;
        // Mozilla/5.0 (iPhone; CPU iPhone OS 16_6 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) CriOS/116.0.5845.177 Mobile/15E148 Safari/604.1
        user_agent: string;
        // India
        ipcountry?: string;
        // Kozhikode
        ipcity?: string;
        // iPhone IOS16
        platform: string;
        // mobile
        device_type: string;
        user_cookie_consent?: string | boolean;
      } & {
        user_name?: string;
        user_Lastname?: string;
        user_email?: string;
        user_phone?: string;
        pr_fs_understand_price?: boolean;
        pr_fs_goal?: string;
        pr_fs_goal_comment?: string;
        requestId?: string;
        sf_record_type_code?: string;
        sf_lead_f_type?: string;
        sf_lead_f_status?: FactsheetStoryblok['programStatus'];
        sf_lead_country?: string;
        timezone: string;
        sf_timezone: string;
      } & { subjects?: string[] };

// Click sbmt btn | form-interaction | user_action
export type LogDataPayload = {
  form_action: FormLogEvent;
  fr_form_title?: string;
  fr_form_id?: string;
  language_form: Locales;
  user_email?: string;
  user_name?: string;
  user_phone?: string;
};

export type FormEventsState = {
  [event in FormLogEvent]: 'waiting' | 'pending' | 'complete' | 'error';
};

export type FormLogEvent =
  | 'Form loaded but invisible'
  | 'Form visible'
  | 'Name field interaction'
  | 'Email field interaction'
  | 'Phone field interaction'
  | 'Check interaction'
  | 'Dropdown interaction'
  | 'Check privacy policy'
  | 'Click on submit button'
  | 'Form submitted success'
  | 'Form submitted failed'
  | 'Email validation error' // custom
  | 'Form field interactions updated' // custom
  | 'File upload failed'; // custom

export type LogFormEventData = {
  attributes?: FormRequestData;
  'RENTA response'?: string;
};

export type LogData = LogFormEventData & {
  timestamp: number;
  message: FormLogEvent;
};

export type FileUploadResponse = {
  status: 'idle' | 'loading' | 'success' | 'error';
  progress: number;
};

export type FileUploadsState = Record<string, FileUploadResponse>;

export interface DeviceInfo {
  data: {
    vendor?: string;
    model?: string;
    marketingName?: string;
    yearReleased?: string;
    primaryHardwareType?: string;
    osName?: string;
  };
}

export const FORM_TYPE_MAP = {
  FS: 'Factsheet',
  Meeting: 'Meeting',
  Price: 'Price',
  Partnership: 'Partnership',
  RE: 'Real estate',
  QN: 'Questionnaire',
  IMIN: 'KYC',
  Guide: 'Guide',
} as const;

export const FORM_UI_ELEMENT_MAP = {
  FS: 'inline',
  RE: 'popup',
  DEFAULT: 'new_window',
} as const;

export type FormType = keyof typeof FORM_TYPE_MAP;
export type FormUiElement =
  (typeof FORM_UI_ELEMENT_MAP)[keyof typeof FORM_UI_ELEMENT_MAP];
